import { useQueries } from "@tanstack/react-query";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import moment from "moment/moment";
import React, { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import { Link, Navigate } from "react-router-dom";
import useDetails from "../../hooks/useDetails";
import useSlider from "../../hooks/useSlider";
import {
  getItemPreparedListing,
  getProductWasteTracking,
  getReports,
} from "../../services/services";
import { getMonthName } from "../../utils/common";
import { constant } from "../../utils/constants";
import Pagination from "../../utils/Pagination";
import Sidebar from "../sidebar/Sidebar";
import "./../../pages/Usermanagement/addNewUser.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const options = {
  responsive: true,
  plugins: {
    legend: false,
    title: {
      display: true,
      text: "Total Dollars Spent",
    },
  },
};

const getValue = (item, keys, fallback = "-") => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return fallback;
};

const formatCurrency = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? `$${amount.toFixed(2)}` : "-";
};

const getProductWasteCostOfGoods = (item) => {
  const wasted = Number(item?.wasted ?? 0);
  const costOfGoods = Number(item?.itemDetails?.wasteTracking?.costOfGoods ?? 0);

  return wasted * costOfGoods;
};

const Reportsindex = () => {
  const details = useDetails();
  const isSlider = useSlider();
  const permissions = details?.userPermissions;
  const hasReportsPermission =
    !!permissions?.[constant.PERMISSIONS.CAN_GENERATE_REPORTS];
  const hasAnalyticsPermission =
    !!permissions?.[constant.PERMISSIONS.CAN_VIEW_ANALYTICS];
  const permissionsLoaded = !!permissions;
  const canViewPage = hasReportsPermission || hasAnalyticsPermission;
  const reportParams = {
    page: constant.PAGE_ONE,
    limit: constant.PER_PAGE_TEN,
  };
  const [wasteTrackingPage, setWasteTrackingPage] = useState(
    constant.PAGE_ONE
  );
  const [wasteTrackingLimit, setWasteTrackingLimit] = useState(
    constant.PER_PAGE_TEN
  );
  const [itemPreparedPage, setItemPreparedPage] = useState(constant.PAGE_ONE);
  const [itemPreparedLimit, setItemPreparedLimit] = useState(
    constant.PER_PAGE_TEN
  );
  const queryResults = useQueries({
    queries: [
      {
        queryKey: ["transactions", details?.companyUID],
        enabled: !!details?.companyUID && canViewPage,
        queryFn: async () => {
          const resp = await getReports({
            ...reportParams,
            groupBy: "month",
            companyId: details?.companyUID,
          });
          return resp?.data?.data ?? {};
        },
      },
      {
        queryKey: [
          "product-waste-tracking",
          details?.companyUID,
          hasAnalyticsPermission,
          wasteTrackingPage,
          wasteTrackingLimit,
        ],
        enabled: !!details?.companyUID && hasAnalyticsPermission,
        queryFn: async () => {
          const resp = await getProductWasteTracking(details?.companyUID, {
            page: wasteTrackingPage,
            limit: wasteTrackingLimit,
          });
          return resp?.data?.data ?? {};
        },
      },
      {
        queryKey: [
          "item-prepared-listing",
          details?.companyUID,
          hasAnalyticsPermission,
          itemPreparedPage,
          itemPreparedLimit,
        ],
        enabled: !!details?.companyUID && hasAnalyticsPermission,
        queryFn: async () => {
          const resp = await getItemPreparedListing(details?.companyUID, {
            page: itemPreparedPage,
            limit: itemPreparedLimit,
          });
          return resp?.data?.data ?? {};
        },
      },
    ],
  });

  const [reports, wasteTrackingReport, itemPreparedReport] = queryResults;
  const wasteTrackingList = wasteTrackingReport?.data?.trackingList ?? [];
  const itemPreparedList = itemPreparedReport?.data?.printers ?? [];
  
  const data = {
    labels: hasAnalyticsPermission
      ? reports?.data?.analytics?.map((i) => getMonthName(i?.month)) ?? []
      : [],
    datasets: [
      {
        data: hasAnalyticsPermission
          ? reports?.data?.analytics?.map((i) => i?.totalRevenue) ?? []
          : [],
        backgroundColor: "#3a883a",
      },
    ],
  };

  if (permissionsLoaded && !canViewPage) {
    return <Navigate to="/not-allowed" replace={true} />;
  }
  
  return (
    <div className="mainbox">
      <Sidebar />
      <div className={isSlider ? "body-content close" : "body-content open"}>
        <Row>
          <Col xl={6} className="">
            <h2 className="mainhead font-24">Reports & Analytics</h2>
          </Col>
          {/* <Col xl={6}>
            <div className="d-flex align-items-center justify-content-end">
              <label className="labelText">Filter by:</label>
              <select
                className="form-select selectfeild"
                aria-label="Default select example"
              >
                <option selected>Weekly</option>
                <option value="1">Today</option>
                <option value="2">Yesterday</option>
                <option value="3">Weekly</option>
                <option value="3">Monthly</option>
                <option value="3">Every 3 Month</option>
                <option value="3">Every 6 Month</option>
                <option value="3">Every 9 month</option>
                <option value="3">Yearly</option>
              </select>
              <button className="userBtn btn-theme">Export Reports</button>
            </div>
          </Col> */}
        </Row>
        {(hasReportsPermission || hasAnalyticsPermission) && (
          <Row>
            {hasReportsPermission && (
              <Col className="tableOuter" xl={hasAnalyticsPermission ? 9 : 12}>
                <div className="d-flex align-items-baseline justify-content-between">
                  <label className="font-18 colorDark pb-3">Transactions</label>
                  <p className="mb-0 text-end ">
                    <Link className="text-decoration-none linktext">
                      View All
                    </Link>
                  </p>
                </div>
                <div className="table-responsive tableOut">
                  <table className="table tableDashboard">
                    <thead className="thead-dark">
                      <tr>
                        <th>Order ID</th>
                        <th>User Name</th>
                        <th>Amount</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports?.data?.transactions?.transactions &&
                      reports?.data?.transactions?.transactions?.length > 0 ? (
                        reports?.data?.transactions?.transactions?.map(
                          (item, index) => {
                            return (
                              <tr key={index}>
                                <td>{item?.orderId}</td>
                                <td>{item?.userDetails?.userInfo?.name}</td>
                                <td>${item?.amount}</td>
                                <td>{moment(item?.createdAt).format("lll")}</td>
                                <td>
                                  <p
                                    className={
                                      item?.status ==
                                      constant?.TRANSACTION_STATUS?.SUCCESS
                                        ? "greenBox"
                                        : item?.status ==
                                          constant?.TRANSACTION_STATUS?.PENDING
                                        ? "greenBox yellowBox"
                                        : "greenBox redBox"
                                    }
                                  >
                                    {item?.status ==
                                    constant?.TRANSACTION_STATUS?.SUCCESS
                                      ? "Completed"
                                      : item?.status ==
                                        constant?.TRANSACTION_STATUS?.PENDING
                                      ? "Pending"
                                      : "Cancelled"}
                                  </p>
                                </td>
                              </tr>
                            );
                          }
                        )
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center">
                            No Transaction Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </Col>
            )}
            {hasAnalyticsPermission && (
              <Col xl={hasReportsPermission ? 3 : 12}>
                {/* <div className="revenewImg mt-4"> */}
                <Bar options={options} data={data} />
                {/* <img src="./../images/revenew.png" className="img-fluid"></img> */}
                {/* </div> */}
              </Col>
            )}
          </Row>
        )}
        {hasAnalyticsPermission && (
          <>
            <Row>
              <Col className="tableOuter" xl={12}>
                <div className="d-flex align-items-baseline justify-content-between">
                  <label className="font-18 colorDark pb-3">
                    Product Waste Tracking
                  </label>
                  {/* <p className="mb-0 text-end ">
                <Link className="text-decoration-none linktext">View All</Link>
              </p> */}
                </div>
                <div className="table-responsive tableOut">
                  <table className="table tableDashboard">
                    <thead className="thead-dark">
                      <tr>
                        <th>Item Name</th>
                        <th>Product Quantity</th>
                        <th>Product Waste</th>
                        <th>Cost of Goods</th>
                        <th>Unit of Measure</th>
                        <th>Net Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wasteTrackingList.length > 0 ? (
                        wasteTrackingList.map((item, index) => {
                          const itemName =
                            item?.itemDetails?.en_name ||
                            getValue(item, ["itemName", "labelName", "name"]);
                          const unitOfMeasure = getValue(item, [
                            "measureUnit",
                            "unitOfMeasure",
                          ]);
                          const netWeight = getValue(item, ["netWeight"]);

                          return (
                            <tr key={item?._id || index}>
                              <td>{itemName}</td>
                              <td>{getValue(item, ["quantity"])}</td>
                              <td>{getValue(item, ["wasted"])}</td>
                              <td>
                                {formatCurrency(getProductWasteCostOfGoods(item))}
                              </td>
                              <td>{unitOfMeasure}</td>
                              <td>{netWeight ?? "-"}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center">
                            No Product Waste Tracking Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={wasteTrackingPage}
                  setPage={setWasteTrackingPage}
                  limit={wasteTrackingLimit}
                  setLimit={setWasteTrackingLimit}
                  totalPages={wasteTrackingReport?.data?.total_pages}
                  total={wasteTrackingReport?.data?.total}
                  length={wasteTrackingList?.length ?? 0}
                />
              </Col>
            </Row>
            <Row>
              <Col className="tableOuter" xl={12}>
                <div className="d-flex align-items-baseline justify-content-between">
                  <label className="font-18 colorDark pb-3">Item Prepared</label>
                  {/* <p className="mb-0 text-end ">
                <Link className="text-decoration-none linktext">View All</Link>
              </p> */}
                </div>
                <div className="table-responsive tableOut">
                  <table className="table tableDashboard">
                    <thead className="thead-dark">
                      <tr>
                        <th>Item Name</th>
                        <th>User or Employee</th>
                        <th>Location</th>
                        <th>Quantity of Labels Printed</th>
                        <th>Date & Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemPreparedList.length > 0 ? (
                        itemPreparedList.map((item, index) => {
                          const preparedBy =
                            item?.userDetails?.[0]?.userInfo?.name ||
                            item?.userDetails?.[0]?.username ||
                            item?.employeeName ||
                            "-";
                          const locationName =
                            item?.locationDetails?.[0]?.name ||
                            getValue(item, ["locationName", "location"]);

                          return (
                            <tr key={item?._id || index}>
                              <td>{item?.itemName || item?.labelName || "-"}</td>
                              <td>{preparedBy}</td>
                              <td>{locationName}</td>
                              <td>{item?.labelQnt ?? "-"}</td>
                              <td>
                                {item?.preparedOn
                                  ? moment(item?.preparedOn).format("lll")
                                  : "-"}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10} className="text-center">
                            No Item Prepared Found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={itemPreparedPage}
                  setPage={setItemPreparedPage}
                  limit={itemPreparedLimit}
                  setLimit={setItemPreparedLimit}
                  totalPages={itemPreparedReport?.data?.total_pages}
                  total={itemPreparedReport?.data?.total}
                  length={itemPreparedList?.length ?? 0}
                />
              </Col>
            </Row>
          </>
        )}
      </div>
    </div>
  );
};

export default Reportsindex;
